<template>
  <div v-if="showDebug" class="debug-panel">
    <div class="debug-header" @click="collapsed = !collapsed">
      <span class="debug-title">🔧 Development Debug Panel</span>
      <span class="debug-toggle">{{ collapsed ? '▼' : '▲' }}</span>
    </div>

    <div v-if="!collapsed" class="debug-content">
      <!-- Route Info -->
      <div class="debug-section">
        <h4>Route Information</h4>
        <table>
          <tr>
            <td><strong>Current Path:</strong></td>
            <td>{{ currentPath }}</td>
          </tr>
          <tr>
            <td><strong>Company Slug:</strong></td>
            <td>{{ companySlug || '(default/mission)' }}</td>
          </tr>
          <tr>
            <td><strong>Full URL:</strong></td>
            <td>{{ fullUrl }}</td>
          </tr>
        </table>
      </div>

      <!-- Clerk Raw Response -->
      <div class="debug-section">
        <h4>🔌 Clerk API Response</h4>
        <div class="debug-loading" v-if="configLoading">
          Loading from Clerk...
        </div>
        <div v-else-if="clerkRawResponse">
          <table>
            <tr>
              <td><strong>Source:</strong></td>
              <td>{{ clerkRawResponse.source }}</td>
            </tr>
            <tr v-if="clerkRawResponse.url">
              <td><strong>API Endpoint:</strong></td>
              <td class="debug-truncate">{{ clerkRawResponse.url }}</td>
            </tr>
            <tr v-if="clerkRawResponse.status">
              <td><strong>Status:</strong></td>
              <td :class="clerkRawResponse.status === 200 ? 'status-success' : 'status-error'">
                {{ clerkRawResponse.status }}
              </td>
            </tr>
            <tr v-if="clerkRawResponse.error">
              <td><strong>Error:</strong></td>
              <td class="status-error">{{ clerkRawResponse.error }}</td>
            </tr>
          </table>

          <!-- Raw Clerk Response JSON -->
          <div class="debug-json" v-if="clerkRawResponse.data || clerkRawResponse.error">
            <button @click="showClerkJson = !showClerkJson" class="debug-json-toggle">
              {{ showClerkJson ? 'Hide' : 'Show' }} Raw Clerk Response
            </button>
            <pre v-if="showClerkJson" class="debug-json-content">{{ formattedClerkResponse }}</pre>
          </div>
        </div>
        <div v-else>
          <p style="color: #ffaa00;">No Clerk response data available</p>
        </div>
      </div>

      <!-- Company Config (Processed) -->
      <div class="debug-section">
        <h4>⚙️ Processed Company Configuration</h4>
        <div class="debug-loading" v-if="configLoading">
          Loading configuration...
        </div>
        <div v-else>
          <table>
            <tr>
              <td><strong>Company Name:</strong></td>
              <td>{{ companyConfig?.companyName || 'N/A' }}</td>
            </tr>
            <tr>
              <td><strong>Company Slug:</strong></td>
              <td>{{ companyConfig?.companySlug || 'N/A' }}</td>
            </tr>
            <tr>
              <td><strong>Office IDs:</strong></td>
              <td>{{ companyConfig?.integration?.officeIDs || 'N/A' }}</td>
            </tr>
            <tr>
              <td><strong>Source ID:</strong></td>
              <td>{{ companyConfig?.integration?.sourceID || 'N/A' }}</td>
            </tr>
            <tr>
              <td><strong>Primary Color:</strong></td>
              <td>
                <span class="color-box" :style="{ backgroundColor: companyConfig?.theme?.colors?.primary }"></span>
                {{ companyConfig?.theme?.colors?.primary || 'N/A' }}
              </td>
            </tr>
          </table>

          <!-- Full Processed Config JSON -->
          <div class="debug-json">
            <button @click="showFullJson = !showFullJson" class="debug-json-toggle">
              {{ showFullJson ? 'Hide' : 'Show' }} Full Processed Config
            </button>
            <pre v-if="showFullJson" class="debug-json-content">{{ formattedConfig }}</pre>
          </div>
        </div>
      </div>

      <!-- LocalStorage Keys -->
      <div class="debug-section">
        <h4>LocalStorage Keys (for this company)</h4>
        <table>
          <tr v-for="key in localStorageKeys" :key="key">
            <td><strong>{{ key }}:</strong></td>
            <td class="debug-truncate">{{ getLocalStorageValue(key) }}</td>
          </tr>
        </table>
      </div>

      <!-- Environment Info -->
      <div class="debug-section">
        <h4>Environment</h4>
        <table>
          <tr>
            <td><strong>Mode:</strong></td>
            <td>{{ nodeEnv }}</td>
          </tr>
          <tr>
            <td><strong>API Base URL:</strong></td>
            <td>{{ apiBaseUrl }}</td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { companyConfig, configLoading, clerkRawResponse } from '@/services/companyConfig';

export default {
  name: 'DebugPanel',
  props: {
    companySlug: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      collapsed: true,
      showFullJson: false,
      showClerkJson: false,
      showDebug: process.env.VUE_APP_SHOW_DEBUG === 'true'
    }
  },
  computed: {
    companyConfig() {
      return companyConfig.value;
    },
    configLoading() {
      return configLoading.value;
    },
    clerkRawResponse() {
      return clerkRawResponse.value;
    },
    currentPath() {
      return this.$route?.path || window.location.pathname;
    },
    fullUrl() {
      return window.location.href;
    },
    nodeEnv() {
      return process.env.NODE_ENV;
    },
    apiBaseUrl() {
      return process.env.NODE_ENV === 'development'
        ? 'http://localhost:3000/api'
        : 'https://api.getpestcontrol.services/api';
    },
    formattedConfig() {
      return JSON.stringify(this.companyConfig, null, 2);
    },
    formattedClerkResponse() {
      if (this.clerkRawResponse?.data) {
        return JSON.stringify(this.clerkRawResponse.data, null, 2);
      }
      if (this.clerkRawResponse?.error) {
        return JSON.stringify(this.clerkRawResponse.error, null, 2);
      }
      return 'No data';
    },
    localStorageKeys() {
      const slug = this.companySlug || 'mission';
      return [
        `${slug}-place`,
        `${slug}-customer`,
        `${slug}-routes`,
        `${slug}-appointment`,
        `${slug}-billing`
      ];
    }
  },
  methods: {
    getLocalStorageValue(key) {
      const value = localStorage.getItem(key);
      if (!value) return '(empty)';
      try {
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed).substring(0, 100) + '...';
      } catch (e) {
        return value.substring(0, 100) + '...';
      }
    }
  }
}
</script>

<style scoped>
.debug-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #1a1a1a;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  border-top: 3px solid #00ff00;
  box-shadow: 0 -4px 6px rgba(0, 0, 0, 0.3);
  z-index: 9999;
  max-height: 70vh;
  overflow-y: auto;
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background: #0d0d0d;
  cursor: pointer;
  user-select: none;
}

.debug-header:hover {
  background: #1f1f1f;
}

.debug-title {
  font-weight: bold;
  font-size: 14px;
}

.debug-toggle {
  font-size: 16px;
}

.debug-content {
  padding: 15px 20px;
}

.debug-section {
  margin-bottom: 20px;
  padding: 15px;
  background: #0d0d0d;
  border: 1px solid #00ff00;
  border-radius: 4px;
}

.debug-section h4 {
  margin: 0 0 10px 0;
  color: #00ff00;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.debug-section table {
  width: 100%;
  border-collapse: collapse;
}

.debug-section table td {
  padding: 5px 10px;
  vertical-align: top;
}

.debug-section table td:first-child {
  color: #00aaff;
  width: 200px;
}

.debug-truncate {
  max-width: 500px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-success {
  color: #00ff00;
  font-weight: bold;
}

.status-error {
  color: #ff4444;
  font-weight: bold;
}

.debug-loading {
  color: #ffaa00;
  padding: 10px;
  text-align: center;
}

.color-box {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 1px solid #00ff00;
  margin-right: 8px;
  vertical-align: middle;
}

.debug-json {
  margin-top: 15px;
}

.debug-json-toggle {
  background: #00ff00;
  color: #000;
  border: none;
  padding: 8px 16px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  border-radius: 4px;
  transition: background 0.2s;
}

.debug-json-toggle:hover {
  background: #00cc00;
}

.debug-json-content {
  margin-top: 10px;
  background: #000;
  color: #00ff00;
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #00ff00;
}

/* Scrollbar styling */
.debug-panel::-webkit-scrollbar,
.debug-json-content::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.debug-panel::-webkit-scrollbar-track,
.debug-json-content::-webkit-scrollbar-track {
  background: #0d0d0d;
}

.debug-panel::-webkit-scrollbar-thumb,
.debug-json-content::-webkit-scrollbar-thumb {
  background: #00ff00;
  border-radius: 4px;
}

.debug-panel::-webkit-scrollbar-thumb:hover,
.debug-json-content::-webkit-scrollbar-thumb:hover {
  background: #00cc00;
}
</style>
